import type { ProjetoSalvo } from '../types/corte';
import { corrigirGrafiaBerlin } from './corrigirGrafiaBerlin';

const ZUSTAND_VERSION = 0;
const CHAVE_CATALOGO_EXCLUIDOS = 'catalogo-projetos-excluidos-ade';

export interface EstadoCatalogoPersistido {
  projetosSalvos: ProjetoSalvo[];
  configuracao: unknown;
  materialConfig: unknown;
}

function normalizarChaveCatalogo(s: string): string {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();
}

/** Unicidade no catálogo: mesmo produto + mesma peça = um projeto só. */
export function chaveParProjetoCatalogo(nomeProduto: string, nomePeca: string): string {
  return `${normalizarChaveCatalogo(nomeProduto)}|${normalizarChaveCatalogo(nomePeca)}`;
}

export function lerExclusoesCatalogo(): { ids: Set<string>; pares: Set<string> } {
  try {
    const raw = localStorage.getItem(CHAVE_CATALOGO_EXCLUIDOS);
    if (!raw) return { ids: new Set(), pares: new Set() };
    const parsed = JSON.parse(raw);
    const ids = new Set(
      (Array.isArray(parsed?.ids) ? parsed.ids : [])
        .map((x: unknown) => String(x || '').trim())
        .filter(Boolean)
    );
    const pares = new Set(
      (Array.isArray(parsed?.pares) ? parsed.pares : [])
        .map((x: unknown) => String(x || '').trim())
        .filter(Boolean)
    );
    return { ids, pares };
  } catch {
    return { ids: new Set(), pares: new Set() };
  }
}

function gravarExclusoesCatalogo(ids: Set<string>, pares: Set<string>): void {
  try {
    localStorage.setItem(
      CHAVE_CATALOGO_EXCLUIDOS,
      JSON.stringify({
        ids: [...ids].slice(-4000),
        pares: [...pares].slice(-4000),
      })
    );
  } catch {
    /* quota */
  }
}

export function registrarExclusaoProjetoCatalogo(projeto: {
  id?: string;
  nomeProduto?: string;
  nomePeca?: string;
}): void {
  const { ids, pares } = lerExclusoesCatalogo();
  const id = String(projeto.id || '').trim();
  if (id) ids.add(id);
  const par = chaveParProjetoCatalogo(
    String(projeto.nomeProduto || ''),
    String(projeto.nomePeca || '')
  );
  if (par !== '|') pares.add(par);
  gravarExclusoesCatalogo(ids, pares);
}

export function liberarExclusaoProjetoCatalogo(nomeProduto: string, nomePeca: string, id?: string): void {
  const { ids, pares } = lerExclusoesCatalogo();
  let mudou = false;
  const idLimpo = String(id || '').trim();
  if (idLimpo && ids.delete(idLimpo)) mudou = true;
  const par = chaveParProjetoCatalogo(nomeProduto, nomePeca);
  if (par !== '|' && pares.delete(par)) mudou = true;
  if (mudou) gravarExclusoesCatalogo(ids, pares);
}

export function filtrarProjetosCatalogoNaoExcluidos(lista: ProjetoSalvo[]): ProjetoSalvo[] {
  const { ids, pares } = lerExclusoesCatalogo();
  if (ids.size === 0 && pares.size === 0) return lista;
  return lista.filter((p) => {
    const id = String(p.id || '').trim();
    if (id && ids.has(id)) return false;
    const par = chaveParProjetoCatalogo(p.nomeProduto, p.nomePeca);
    if (par !== '|' && pares.has(par)) return false;
    return true;
  });
}

function parseEstadoCatalogo(raw: string | null): EstadoCatalogoPersistido | null {
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as {
      state?: {
        projetosSalvos?: ProjetoSalvo[];
        configuracao?: unknown;
        materialConfig?: unknown;
      };
    };
    if (!p?.state || typeof p.state !== 'object') return null;
    return {
      projetosSalvos: Array.isArray(p.state.projetosSalvos) ? p.state.projetosSalvos : [],
      configuracao: p.state.configuracao ?? null,
      materialConfig: p.state.materialConfig ?? null,
    };
  } catch {
    return null;
  }
}

export function contarProjetosCatalogo(raw: string | null): number {
  return parseEstadoCatalogo(raw)?.projetosSalvos.length ?? 0;
}

/** Une projetos: um por produto+peça (última lista prevalece); respeita exclusões. */
export function mesclarProjetosCatalogo(
  ...listas: ProjetoSalvo[][]
): ProjetoSalvo[] {
  const porPar = new Map<string, ProjetoSalvo>();
  for (const lista of listas) {
    for (const p of lista) {
      if (!p?.id) continue;
      const projeto: ProjetoSalvo = {
        ...p,
        nomeProduto: corrigirGrafiaBerlin(String(p.nomeProduto || '')),
        nomePeca: corrigirGrafiaBerlin(String(p.nomePeca || '')),
      };
      const par = chaveParProjetoCatalogo(projeto.nomeProduto, projeto.nomePeca);
      porPar.set(par, projeto);
    }
  }
  return filtrarProjetosCatalogoNaoExcluidos([...porPar.values()]).sort((a, b) =>
    String(a.nomeProduto || '').localeCompare(String(b.nomeProduto || ''), 'pt-BR')
  );
}

/** Escolhe config/material da fonte com mais projetos (geralmente a mais completa). */
export function mesclarEstadoCatalogo(
  ...fontes: (string | null)[]
): string | null {
  const estados = fontes
    .map(parseEstadoCatalogo)
    .filter((e): e is EstadoCatalogoPersistido => e !== null);
  if (estados.length === 0) return null;

  const projetosSalvos = mesclarProjetosCatalogo(...estados.map((e) => e.projetosSalvos));
  const base = [...estados].sort(
    (a, b) => b.projetosSalvos.length - a.projetosSalvos.length
  )[0];

  return JSON.stringify({
    state: {
      projetosSalvos,
      configuracao: base.configuracao,
      materialConfig: base.materialConfig,
    },
    version: ZUSTAND_VERSION,
  });
}

export function lerLegadoCatalogoLocalStorage(chave: string): string | null {
  try {
    return localStorage.getItem(chave);
  } catch {
    return null;
  }
}

export function gravarLegadoCatalogoLocalStorage(chave: string, valor: string): void {
  try {
    localStorage.setItem(chave, valor);
  } catch {
    /* quota */
  }
}

/** Normaliza JSON do Zustand antes de gravar (dedup produto+peça, sem mesclar backup). */
export function normalizarJsonCatalogoParaGravacao(raw: string): string {
  try {
    const p = JSON.parse(raw) as {
      state?: {
        projetosSalvos?: ProjetoSalvo[];
        configuracao?: unknown;
        materialConfig?: unknown;
      };
    };
    if (!p?.state || typeof p.state !== 'object') return raw;
    return serializarEstadoCatalogoAtual({
      projetosSalvos: Array.isArray(p.state.projetosSalvos) ? p.state.projetosSalvos : [],
      configuracao: p.state.configuracao ?? null,
      materialConfig: p.state.materialConfig ?? null,
    });
  } catch {
    return raw;
  }
}

/** Estado salvo pelo usuário — não mescla cópias antigas (evita ressuscitar apagados). */
export function serializarEstadoCatalogoAtual(estado: EstadoCatalogoPersistido): string {
  const projetosSalvos = filtrarProjetosCatalogoNaoExcluidos(
    mesclarProjetosCatalogo(estado.projetosSalvos)
  );
  return JSON.stringify({
    state: {
      projetosSalvos,
      configuracao: estado.configuracao,
      materialConfig: estado.materialConfig,
    },
    version: ZUSTAND_VERSION,
  });
}
